import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Platform, Pressable, View, ActivityIndicator, PanResponder, Image as RNImage, Alert, ScrollView, TextInput } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import ChatInput from '@/components/ChatInput/ChatInput';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useResponsiveness from '@/hooks/useResponsiveness';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { Image } from 'expo-image';
import { useVideoPlayer, VideoView } from 'expo-video';
import { useEvent } from 'expo';
import { isDarkTheme, getShadowThemeStyles } from '@/constants/Colors';
import { ALLOWED_INPAINTING_FILE_TYPES } from '@/constants/config';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore, useFileStore, useChatStore, useAuthStore, useToastStore } from '@/store';
import socketManager from '@/lib/socket';
import { Svg, Path } from 'react-native-svg';
import { captureRef } from '@/constants/utils/lazyCapture';
import * as Clipboard from 'expo-clipboard';
import AudioWaveformPlayer from '@/components/ChatInput/AudioWaveformPlayer';
import Model3DViewer from '@/components/Model3D/Model3DViewer';
import { downloadMedia, downloadAndShare, isIOSSafari } from '@/constants/utils/mediaDownload';
import { download3DAsset } from '@/constants/utils/model3DDownload';
import { useTranslation } from '@/hooks/useTranslation';
import { useRouter } from 'expo-router';
import MediaImagePreview from '@/components/Media/MediaImagePreview';

const MODEL_3D_EXT_REGEX = /\.(glb|gltf|obj|fbx|usdz|ply|stl)(\?.*)?$/i;

const createStyles = (isSM, insets, colors, theme) => StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.solid_color_primary,
    zIndex: 9999,
  },
  topRow: {
    position: 'absolute',
    top: 0,
    left: insets.left,
    right: insets.right,
    height: 53 + insets.top,
    paddingHorizontal: 10,
    paddingTop: 5 + insets.top,
    zIndex: 10000,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  topRowLeft: {
    flexDirection: 'row',
    gap: 2,
  },
  topRowRight: {
    flexDirection: 'row',
    gap: 2,
  },
  topRowCenter: {
    position: 'absolute',
    left: insets.left,
    right: insets.right,
    top: 20 + insets.top,
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  sidebar: {
    position: 'absolute',
    top: 53 + insets.top,
    right: 0,
    bottom: 0,
    width: 96,
    paddingTop: 20,
    paddingBottom: 8,
    paddingRight: 8,
    paddingLeft: 8,
    backgroundColor: getShadowThemeStyles(theme).primaryOpaque(0.35),
  },
  sidebarBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 66,
    paddingTop: 0,
    paddingBottom: 10,
    paddingHorizontal: 10,
    backgroundColor: getShadowThemeStyles(theme).primaryOpaque(0.35),
  },
  sidebarScroll: {
    flexGrow: 0,
  },
  thumbItem: {
    marginBottom: 8,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    alignSelf: 'center',
  },
  sidebarGradientTop: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 40,
    pointerEvents: 'none',
  },
  sidebarGradientBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 40,
    pointerEvents: 'none',
  },
  imageContainer: {
    position: 'relative',
    width: isSM ? '100%' : '90%',
    height: '90%',
    marginTop: 53 + insets.top,
    borderRadius: isSM ? 0 : 18,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    alignSelf: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: isSM ? 0 : 18,
    ...(Platform.OS === 'web' ? {
      userSelect: 'none',
      MozUserSelect: 'none',
      WebkitUserSelect: 'none',
      msUserSelect: 'none',
    } : {}),
  },
  actionButton: {
    marginRight: 0,
  },
  activityIndicator: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  drawOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  hiddenMaskShot: {
    position: 'absolute',
    left: -99999,
    top: -99999,
  },
  drawArea: {
    position: 'absolute',
  },
  brushPreview: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: '#ffffff',
    backgroundColor: 'transparent',
    opacity: 0.9,
    pointerEvents: 'none',
  },
  paperCanvas: {
    position: 'absolute',
    left: 0,
    top: 0,
    opacity: 0.9,
    pointerEvents: 'none',
  },
  chatInputContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: isSM ? 80 : 35,
    paddingHorizontal: isSM ? 10 : 16,
    paddingTop: 8,
    alignItems: 'center',
  },
  chatInputInner: {
    width: '100%',
    maxWidth: 680,
  },
  brushSliderWrapper: {
    height: 40,
    marginLeft: 5,
    marginRight: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  brushTrack: {
    height: 6,
    borderRadius: 10,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'visible',
  },
  brushFill: {
    height: '100%',
    borderRadius: 10,
    backgroundColor: colors.text_color_secondary,
  },
  brushThumb: {
    position: 'absolute',
    top: -7,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.text_color_primary,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.3,
      shadowRadius: 3,
      elevation: 3,
    }),
  },
});

function Lightbox({
  isOpen,
  onClose,
  imageUrl,
  fileId,
  conversationId,
  openToEditor = false,
  dismissOnOutsidePress = true,
  withInpainting,
  withChatInput,
  chatInputProps = {},
}) {

  // 
  // turn off for now
  // 
  withChatInput = false;
  openToEditor = false;
  withInpainting = false;
  // ------------------

  const { isXS, isSM } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const { colors, theme } = useTheme();
  const router = useRouter();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const userId = useAuthStore(state => state.user?.id);
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const conversation = useChatStore(state => conversationId ? state.chats.find(c => c.id === conversationId) : null);
  const getFileById = useFileStore(state => state.actions.getFileById);
  const deleteFile = useFileStore(state => state.actions.deleteFile);
  const updateFile = useFileStore(state => state.actions.updateFile);
  
  // Rename modal state
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  
  // Download state for mobile
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useTranslation();

  // Collect assistant media (images and videos) in this conversation
  const asstMediaInConversation = useMemo(() => {
    try {
      const msgs = conversation?.messages;
      if (!Array.isArray(msgs)) return [];
      const media = [];
      for (let i = 0; i < msgs.length; i++) {
        const m = msgs[i];
        if (m?.role === 'assistant' && Array.isArray(m.files)) {
          for (let j = 0; j < m.files.length; j++) {
            const f = m.files[j];
            const isImage = f && (f.fileType === 'image' || f.type === 'image' || f.type?.startsWith?.('image/'));
            const isVideo = f && (f.fileType === 'video' || f.type === 'video' || f.type?.startsWith?.('video/'));
            const isAudio = f && (f.fileType === 'audio' || f.type === 'audio' || f.type?.startsWith?.('audio/'));
            const is3D = f && (f.fileType === '3d' || f.type === '3d' || MODEL_3D_EXT_REGEX.test(f.cdnPath || f.url || ''));
            if (isImage || isVideo || isAudio || is3D) {
              media.push(f);
            }
          }
        }
      }
      return media;
    } catch (e) {
      return [];
    }
  }, [conversation]);

  const showSidebar = !!asstMediaInConversation?.length;
  const asstUris = useMemo(() => {
    if (!showSidebar) return [];
    const out = [];
    for (let i = 0; i < asstMediaInConversation.length; i++) {
      const f = asstMediaInConversation[i];
      const uri = (f && (f.cdnPath || f.url)) || null;
      if (uri) out.push(uri);
    }
    return out;
  }, [showSidebar, asstMediaInConversation]);
  const styles = useMemo(() => createStyles(isSM, insets, colors, theme), [isSM, insets, colors, theme]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInpainting, setIsInpainting] = useState(false);
  const [strokes, setStrokes] = useState([]); // Each stroke: { points: [{x,y}], width }
  const [redoStack, setRedoStack] = useState([]);
  const [brushSize, setBrushSize] = useState(60);
  const [isErasing, setIsErasing] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const maskRef = useRef(null);
  const containerRef = useRef(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [sliderWidth, setSliderWidth] = useState(120);
  const canvasRef = useRef(null);
  const [paperReady, setPaperReady] = useState(false);
  const paperPathsRef = useRef([]);
  const paperRef = useRef(null);
  const [hoverPos, setHoverPos] = useState({ x: -1000, y: -1000 });
  const [isHovering, setIsHovering] = useState(false);
  const gradientPhaseRef = useRef(0);
  const gradientAnimIdRef = useRef(null);

  const effectiveUrl = useMemo(() => {
    let url = imageUrl;
    if (!url && fileId) {
      try {
        const f = getFileById && getFileById(fileId);
        if (f && f.cdnPath) url = f.cdnPath;
      } catch (e) {}
    }
    return url || null;
  }, [imageUrl, fileId, getFileById]);

  const [currentUrl, setCurrentUrl] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  useEffect(() => {
    setCurrentUrl(effectiveUrl);
  }, [effectiveUrl]);

  // Track current file when available for type detection
  useEffect(() => {
    try {
      // Prefer matching the currently selected URL to allow navigation between items
      if (currentUrl && conversation) {
        const msgs = conversation?.messages || [];
        for (let i = 0; i < msgs.length; i++) {
          const m = msgs[i];
          const files = Array.isArray(m.files) ? m.files : [];
          for (let j = 0; j < files.length; j++) {
            const f = files[j];
            const uri = (f && (f.cdnPath || f.url)) || null;
            if (uri && uri === currentUrl) {
              setCurrentFile(f);
              return;
            }
          }
        }
        // If not found by URL, fall back to fileId when available
        if (fileId) {
          const f = getFileById && getFileById(fileId);
          setCurrentFile(f || null);
          return;
        }
        setCurrentFile(null);
        return;
      }
      // No currentUrl: fall back to fileId
      if (fileId) {
        const f = getFileById && getFileById(fileId);
        setCurrentFile(f || null);
      } else {
        setCurrentFile(null);
      }
    } catch (e) {
      setCurrentFile(null);
    }
  }, [fileId, getFileById, currentUrl, conversation]);

  const isCurrentVideo = useMemo(() => {
    const f = currentFile;
    if (f) return f.fileType === 'video' || f.type === 'video' || f.type?.startsWith?.('video/');
    // Fallback: infer from URL extension when file is not resolved
    const u = currentUrl || '';
    return /\.(mp4|mov|m4v|webm|mkv)(\?.*)?$/i.test(u);
  }, [currentFile, currentUrl]);

  const isCurrentImage = useMemo(() => {
    const f = currentFile;
    const u = currentUrl || '';
    // If the URL clearly points to non-image media, don't claim it's an image.
    if (!f) {
      if (MODEL_3D_EXT_REGEX.test(u)) return false;
      if (/\.(mp4|mov|m4v|webm|mkv)(\?.*)?$/i.test(u)) return false;
      if (/\.(mp3|m4a|aac|wav|ogg|oga|flac)(\?.*)?$/i.test(u)) return false;
      return true;
    }
    return f.fileType === 'image' || f.type === 'image' || f.type?.startsWith?.('image/');
  }, [currentFile, currentUrl]);

  const isCurrentAudio = useMemo(() => {
    const f = currentFile;
    if (f) return f.fileType === 'audio' || f.type === 'audio' || f.type?.startsWith?.('audio/');
    const u = currentUrl || '';
    return /\.(mp3|m4a|aac|wav|ogg|oga|flac)(\?.*)?$/i.test(u);
  }, [currentFile, currentUrl]);

  const isCurrent3D = useMemo(() => {
    const f = currentFile;
    if (f && (f.fileType === '3d' || f.type === '3d')) return true;
    const u = currentUrl || '';
    return MODEL_3D_EXT_REGEX.test(u);
  }, [currentFile, currentUrl]);

  const current3DAlternateFormats = useMemo(() => {
    const meta = currentFile?.metadata?.['3d'] || currentFile?.metadata?.threed || {};
    return Array.isArray(meta.alternateFormats) ? meta.alternateFormats : [];
  }, [currentFile]);

  const current3DUsdzSrc = useMemo(() => {
    const usdz = current3DAlternateFormats.find(f => (f.format || '').toLowerCase() === 'usdz');
    return usdz?.url || null;
  }, [current3DAlternateFormats]);

  const current3DFormat = useMemo(() => {
    const meta = currentFile?.metadata?.['3d'] || currentFile?.metadata?.threed || {};
    if (meta.format) return String(meta.format).toLowerCase();
    const u = currentUrl || '';
    const cleaned = u.split('?')[0].split('#')[0];
    const ext = cleaned.split('.').pop()?.toLowerCase();
    return ext || null;
  }, [currentFile, currentUrl]);

  // Inpainting only works on web (uses Paper.js for drawing)
  const canInpaint = useMemo(() => {
    if (Platform.OS !== 'web') return false; // Only supported on web
    if (!withInpainting) return false;
    if (currentFile) {
      const type = currentFile.fileType || currentFile.type;
      return ALLOWED_INPAINTING_FILE_TYPES.some(t => type === t || type?.startsWith?.(t + '/'));
    }
    // Fallback if no file object: assume image if it looks like one and image is allowed
    return isCurrentImage && ALLOWED_INPAINTING_FILE_TYPES.includes('image');
  }, [withInpainting, currentFile, isCurrentImage]);

  useEffect(() => {
    if (!currentUrl) return;
    RNImage.getSize(
      currentUrl,
      (width, height) => setImageNaturalSize({ width, height }),
      () => setImageNaturalSize({ width: 0, height: 0 })
    );
  }, [currentUrl]);

  // Prefer file metadata for videos (and use it when available)
  useEffect(() => {
    try {
      const vW = currentFile?.metadata?.video?.width;
      const vH = currentFile?.metadata?.video?.height;
      if (isCurrentVideo && typeof vW === 'number' && typeof vH === 'number' && vW > 0 && vH > 0) {
        setImageNaturalSize({ width: vW, height: vH });
      }
    } catch (e) {}
  }, [isCurrentVideo, currentFile]);

  // When switching media, attempt to pause any active player via event dispatch on VideoContent if needed
  // The VideoContent component already pauses on unmount; nothing more required here

  // If requested, open directly into inpainting mode when the lightbox opens
  useEffect(() => {
    if (isOpen && openToEditor && canInpaint) {
      setIsErasing(false);
      setIsInpainting(true);
    }
  }, [isOpen, openToEditor, canInpaint]);

  const displayedRect = useMemo(() => {
    const { width: cw, height: ch } = containerSize;
    const { width: iw, height: ih } = imageNaturalSize;
    if (cw === 0 || ch === 0 || iw === 0 || ih === 0) return { width: 0, height: 0, left: 0, top: 0 };
    const scale = Math.min(cw / iw, ch / ih);
    const w = iw * scale;
    const h = ih * scale;
    const left = (cw - w) / 2;
    const top = (ch - h) / 2;
    return { width: w, height: h, left, top };
  }, [containerSize, imageNaturalSize]);

  const scaleFactors = useMemo(() => {
    const sx = displayedRect.width > 0 ? imageNaturalSize.width / displayedRect.width : 1;
    const sy = displayedRect.height > 0 ? imageNaturalSize.height / displayedRect.height : 1;
    return { sx, sy };
  }, [displayedRect, imageNaturalSize]);

  const toScaledPath = (points) => {
    const { sx, sy } = scaleFactors;
    if (!points || points.length === 0) return '';
    const scaled = points.map(p => ({ x: p.x * sx, y: p.y * sy }));
    return pointsToPath(scaled);
  };

  const getPaperStrokeColor = useCallback(() => {
    if (!(Platform.OS === 'web' && paperRef.current)) return null;
    const p = paperRef.current;
    const gradient = new p.Gradient([
      // #cfdef3 (207,222,243)
      new p.GradientStop(new p.Color(207/255, 222/255, 243/255, 1), 0),
      // #E0EAFC (224,234,252)
      new p.GradientStop(new p.Color(224/255, 234/255, 252/255, 1), 1),
    ]);
    const width = Math.max(1, displayedRect.width);
    const phase = gradientPhaseRef.current % (width);
    const fromX = -phase * 2; // move slower
    const toX = fromX + width;
    const from = new p.Point(fromX, 0);
    const to = new p.Point(toX, 0);
    return new p.Color(gradient, from, to);
  }, [displayedRect.width]);

  const rebuildPaperFrom = useCallback((list) => {
    if (!(Platform.OS === 'web' && paperRef.current && paperReady)) return;
    try {
      const p = paperRef.current;
      if (p.project && p.project.activeLayer) {
        p.project.activeLayer.removeChildren();
      }
      paperPathsRef.current = [];
      for (let i = 0; i < list.length; i++) {
        const stroke = list[i];
        if (!stroke || !stroke.points || stroke.points.length === 0) continue;
        const path = new p.Path();
        const gradColor = getPaperStrokeColor();
        path.strokeColor = gradColor || new p.Color(1, 1, 1, 1);
        path.strokeWidth = stroke.width || brushSize;
        path.strokeCap = 'round';
        path.strokeJoin = 'round';
        if (stroke.mode === 'erase') {
          path.blendMode = 'destination-out';
        }
        stroke.points.forEach(pt => path.add(new p.Point(pt.x, pt.y)));
        paperPathsRef.current.push(path);
      }
      p.view.update();
    } catch (e) {}
  }, [paperReady, brushSize, getPaperStrokeColor]);

  // Reset inpainting state and clear web canvas
  function resetInpainting() {
    setStrokes([]);
    setRedoStack([]);
    try {
      if (Platform.OS === 'web' && paperRef.current && paperReady) {
        if (paperRef.current.project && paperRef.current.project.activeLayer) {
          paperRef.current.project.activeLayer.removeChildren();
        }
        paperPathsRef.current = [];
        if (paperRef.current.view) paperRef.current.view.update();
      }
    } catch (e) {}
  }

  useEffect(() => {
    if (!(Platform.OS === 'web')) return undefined;
    if (!paperReady) return undefined;
    gradientPhaseRef.current = 0;
    const animate = () => {
      gradientPhaseRef.current += 0.25; // smaller step for smoother motion
      const p = paperRef.current;
      if (p) {
        const gradColor = getPaperStrokeColor();
        if (gradColor) {
          for (let i = 0; i < paperPathsRef.current.length; i++) {
            const path = paperPathsRef.current[i];
            if (path && path.blendMode !== 'destination-out') {
              path.strokeColor = gradColor;
            }
          }
          p.view.update();
        }
      }
      gradientAnimIdRef.current = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(animate) : setTimeout(animate, 16);
    };
    gradientAnimIdRef.current = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(animate) : setTimeout(animate, 16);
    return () => {
      if (typeof cancelAnimationFrame === 'function' && gradientAnimIdRef.current) {
        cancelAnimationFrame(gradientAnimIdRef.current);
      } else if (gradientAnimIdRef.current) {
        clearTimeout(gradientAnimIdRef.current);
      }
      gradientAnimIdRef.current = null;
    };
  }, [paperReady, getPaperStrokeColor]);

  const minBrush = 4;
  const maxBrush = 100;
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
  const valueToPos = (value) => {
    const ratio = (clamp(value, minBrush, maxBrush) - minBrush) / (maxBrush - minBrush);
    return ratio * sliderWidth;
  };
  const posToValue = (pos) => {
    const ratio = clamp(pos, 0, sliderWidth) / sliderWidth;
    return Math.round(minBrush + ratio * (maxBrush - minBrush));
  };
  const updateBrushFromX = (x) => {
    setBrushSize(posToValue(x));
  };
  const sliderResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: (evt) => {
      updateBrushFromX(evt.nativeEvent.locationX);
    },
    onPanResponderMove: (evt) => {
      updateBrushFromX(evt.nativeEvent.locationX);
    },
    onPanResponderRelease: () => {},
    onPanResponderTerminate: () => {},
  }), [sliderWidth]);

  const requestClose = useCallback(() => {
    if (strokes.length > 0) {
      if (Platform.OS === 'web') {
        const ok = window.confirm(t('lightbox.discardCloseMessage'));
        if (ok) {
          try { resetInpainting(); } catch (e) {}
          onClose();
        }
      } else {
        Alert.alert(
          t('lightbox.discardTitle'),
          t('lightbox.discardMessage'),
          [
            { text: t('lightbox.cancel'), style: 'cancel' },
            { text: t('lightbox.discardButton'), style: 'destructive', onPress: () => { try { resetInpainting(); } catch (e) {} onClose(); } },
          ]
        );
      }
    } else {
      onClose();
    }
  }, [strokes.length, onClose]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        requestClose();
        return;
      }
      if (showSidebar && (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowUp')) {
        event.preventDefault();
        const dir = (event.key === 'ArrowRight' || event.key === 'ArrowDown') ? 1 : -1;
        const total = asstUris.length;
        if (!total) return;
        const idx = currentIndex >= 0 && currentIndex < total ? currentIndex : Math.max(0, asstUris.indexOf(currentUrl || ''));
        const nextIndex = ((idx + dir) % total + total) % total; // wrap
        const nextUri = asstUris[nextIndex];
        if (!nextUri) return;
        if (isInpainting && strokes.length > 0) {
          const confirmSwitch = Platform.OS === 'web'
            ? window.confirm(t('lightbox.discardSwitchMessage'))
            : null;
          if (Platform.OS !== 'web') {
            Alert.alert(
              t('lightbox.discardTitle'),
              t('lightbox.discardSwitchMessage'),
              [
                { text: t('lightbox.cancel'), style: 'cancel' },
                { text: t('lightbox.discardButton'), style: 'destructive', onPress: () => { resetInpainting(); setIsInpainting(false); setCurrentIndex(nextIndex); setCurrentUrl(nextUri); } },
              ]
            );
            return;
          }
          if (!confirmSwitch) return;
          resetInpainting();
          setIsInpainting(false);
        }
        setCurrentIndex(nextIndex);
        setCurrentUrl(nextUri);
      }
    };

    if (Platform.OS === 'web' && isOpen) {
      window.addEventListener('keydown', handleEscKey);
      return () => {
        window.removeEventListener('keydown', handleEscKey);
      };
    }
    return undefined;
  }, [isOpen, requestClose, showSidebar, asstUris, currentUrl, isInpainting, strokes.length, currentIndex]);

  // macOS: auto-focus the container so onKeyDown events are received
  useEffect(() => {
    if (Platform.OS === 'macos' && isOpen && containerRef.current) {
      const timer = setTimeout(() => {
        try { containerRef.current?.focus(); } catch (e) {}
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleMacOSKeyDown = useCallback((event) => {
    const key = event.nativeEvent?.key;
    if (key === 'Escape') {
      requestClose();
      return;
    }
    if (showSidebar && (key === 'ArrowRight' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowUp')) {
      const dir = (key === 'ArrowRight' || key === 'ArrowDown') ? 1 : -1;
      const total = asstUris.length;
      if (!total) return;
      const idx = currentIndex >= 0 && currentIndex < total ? currentIndex : Math.max(0, asstUris.indexOf(currentUrl || ''));
      const nextIndex = ((idx + dir) % total + total) % total;
      const nextUri = asstUris[nextIndex];
      if (!nextUri) return;
      if (isInpainting && strokes.length > 0) {
        resetInpainting();
        setIsInpainting(false);
      }
      setCurrentIndex(nextIndex);
      setCurrentUrl(nextUri);
    }
  }, [requestClose, showSidebar, asstUris, currentUrl, isInpainting, strokes.length, currentIndex]);

  // Reset inpainting state when lightbox is closed
  useEffect(() => {
    if (!isOpen) {
      // Stop gradient animation if running
      if (typeof cancelAnimationFrame === 'function' && gradientAnimIdRef.current) {
        cancelAnimationFrame(gradientAnimIdRef.current);
      } else if (gradientAnimIdRef.current) {
        clearTimeout(gradientAnimIdRef.current);
      }
      gradientAnimIdRef.current = null;
      gradientPhaseRef.current = 0;

      // Clear drawing state
      setIsInpainting(false);
      setIsErasing(false);
      setIsHovering(false);
      setHoverPos({ x: -1000, y: -1000 });
      setRedoStack([]);
      setStrokes([]);
      setCurrentIndex(-1);

      // Clear web canvas if present
      try {
        if (Platform.OS === 'web' && paperRef.current) {
          if (paperRef.current.project && paperRef.current.project.activeLayer) {
            paperRef.current.project.activeLayer.removeChildren();
          }
          paperPathsRef.current = [];
          if (paperRef.current.view) paperRef.current.view.update();
        }
      } catch (e) {}
    }
  }, [isOpen]);

  // Initialize or correct currentIndex when list or url changes (only if unset)
  useEffect(() => {
    try {
      if (!showSidebar) { setCurrentIndex(-1); return; }
      const total = asstUris.length;
      if (total === 0) { setCurrentIndex(-1); return; }
      if (currentIndex < 0 || currentIndex >= total) {
        // Prefer matching by fileId when provided
        let idxById = -1;
        if (fileId && Array.isArray(asstMediaInConversation)) {
          idxById = asstMediaInConversation.findIndex(f => {
            try {
              return f && (f.id === fileId || f._id === fileId);
            } catch (e) {
              return false;
            }
          });
        }
        if (idxById >= 0 && idxById < total) {
          setCurrentIndex(idxById);
          return;
        }
        // Fallback to URL match
        const idxByUrl = asstUris.indexOf(currentUrl || '');
        setCurrentIndex(idxByUrl >= 0 ? idxByUrl : -1);
      }
    } catch (e) {}
  }, [showSidebar, asstMediaInConversation, asstUris, currentUrl, currentIndex, fileId]);

  const handleDownload = async () => {
    const mediaUrl = currentUrl || effectiveUrl || imageUrl;
    if (!mediaUrl) {
      addToast?.(t('media.downloadFailed'), 'error');
      return;
    }

    const ext = isCurrent3D
      ? (current3DFormat || 'glb')
      : (isCurrentVideo ? 'mp4' : (isCurrentAudio ? 'mp3' : 'jpg'));
    const prefix = isCurrent3D
      ? 'model'
      : (isCurrentVideo ? 'video' : (isCurrentAudio ? 'audio' : 'image'));
    const filename = `${prefix}-${Date.now()}.${ext}`;

    setIsDownloading(true);
    setDownloadProgress(0);

    let result;
    if (isCurrent3D) {
      // For OBJ assets, bundle obj+mtl+texture into a zip so the user's local
      // previewer renders textures (Hunyuan ships those at separate URLs).
      // Other 3D formats fall through to plain downloadMedia internally.
      const alternateFormats = currentFile?.metadata?.['3d']?.alternateFormats
        || currentFile?.metadata?.threed?.alternateFormats
        || [];
      result = await download3DAsset({
        modelUrl: mediaUrl,
        alternateFormats,
        baseName: `${prefix}-${Date.now()}`,
        options: {
          onSuccess: () => addToast?.(t('media.downloadComplete'), 'success'),
          onError: (message) => addToast?.(message || t('media.saveFailed'), 'error'),
        },
      });
    } else {
      result = await downloadMedia(mediaUrl, filename, {
        onProgress: (progress) => setDownloadProgress(progress),
        onSuccess: (message) => {
          const toastMsg = Platform.OS === 'web'
            ? t('media.downloadComplete')
            : (message === 'Saved to Photos' ? t('media.savedToPhotos') : t('media.downloadComplete'));
          addToast?.(toastMsg, 'success');
        },
        onError: (message) => addToast?.(message || t('media.downloadFailed'), 'error'),
      });
    }

    setIsDownloading(false);
    setDownloadProgress(null);

    if (result?.needsFallback && Platform.OS === 'web' && isIOSSafari()) {
      openModal('SaveImageInstructionsModal', { imageUrl: mediaUrl });
    }
  };

  const handleShare = async () => {
    const mediaUrl = currentUrl || effectiveUrl || imageUrl;
    if (!mediaUrl) {
      addToast?.(t('media.downloadFailed'), 'error');
      return;
    }

    const ext = isCurrent3D
      ? (current3DFormat || 'glb')
      : (isCurrentVideo ? 'mp4' : (isCurrentAudio ? 'mp3' : 'jpg'));
    const prefix = isCurrent3D
      ? 'model'
      : (isCurrentVideo ? 'video' : (isCurrentAudio ? 'audio' : 'image'));
    const filename = `${prefix}-${Date.now()}.${ext}`;

    setIsDownloading(true);
    setDownloadProgress(0);

    await downloadAndShare(mediaUrl, filename, {
      onProgress: (progress) => setDownloadProgress(progress),
      onSuccess: () => {},
      onError: (message) => addToast?.(message || t('media.downloadFailed'), 'error'),
    });

    setIsDownloading(false);
    setDownloadProgress(null);
  };

  const handleDelete = async () => {
    // Get the current file ID to delete
    const currentFileId = currentIndex >= 0 && asstMediaInConversation[currentIndex]
      ? asstMediaInConversation[currentIndex].id
      : fileId;
    
    if (!currentFileId) {
      addToast?.(t('lightbox.deleteNotFound'), 'error');
      return;
    }

    const confirmDelete = () => {
      deleteFile(currentFileId)
        .then(() => {
          addToast?.(t('lightbox.imageDeleted'), 'success');
          onClose();
        })
        .catch((err) => {
          addToast?.(err.message || t('lightbox.deleteImageFailed'), 'error');
        });
    };

    if (Platform.OS === 'web') {
      if (window.confirm(t('lightbox.deleteImageConfirm'))) {
        confirmDelete();
      }
    } else {
      Alert.alert(
        t('lightbox.deleteImage'),
        t('lightbox.deleteImageConfirm'),
        [
          { text: t('lightbox.cancel'), style: 'cancel' },
          { text: t('lightbox.delete'), style: 'destructive', onPress: confirmDelete },
        ]
      );
    }
  };

  const handleFeedback = () => {
    const imageUrls = imageUrl;
    onClose();
    openModal('FeedbackModal', {
      isOpen: true,
      initialContent: imageUrls,
    });
  };

  const handleOpenRename = useCallback(() => {
    if (currentFile?.name) {
      // Remove extension for editing
      const nameWithoutExt = currentFile.name.replace(/\.[^/.]+$/, '');
      setRenameValue(nameWithoutExt);
    } else {
      setRenameValue(t('lightbox.untitled'));
    }
    setIsRenameModalOpen(true);
  }, [currentFile?.name]);

  const handleCloseRename = useCallback(() => {
    setIsRenameModalOpen(false);
    setRenameValue('');
  }, []);

  const handleSaveRename = useCallback(async () => {
    const currentFileId = currentFile?.id || fileId;
    if (!currentFileId || !renameValue.trim()) return;
    
    try {
      // Get original extension
      const ext = currentFile?.name?.match(/\.[^/.]+$/)?.[0] || '';
      const fullName = renameValue.trim() + ext;
      await updateFile(currentFileId, fullName);
      addToast?.(t('lightbox.trackRenamed'), 'success');
      handleCloseRename();
    } catch (error) {
      console.error('Error renaming file:', error);
      addToast?.(error.message || t('lightbox.trackRenameFailed'), 'error');
    }
  }, [currentFile, fileId, renameValue, updateFile, addToast, handleCloseRename]);

  const startNewStroke = useCallback((x, y) => {
    setStrokes(prev => [...prev, { points: [{ x, y }], width: brushSize, mode: isErasing ? 'erase' : 'draw' }]);
    setRedoStack([]);
  }, [brushSize, isErasing]);

  const addPointToCurrentStroke = useCallback((x, y) => {
    setStrokes(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const current = updated[updated.length - 1];
      updated[updated.length - 1] = { ...current, points: [...current.points, { x, y }] };
      return updated;
    });
  }, []);

  const panResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => isInpainting,
    onMoveShouldSetPanResponder: () => isInpainting,
    onPanResponderGrant: (evt) => {
      const { locationX, locationY } = evt.nativeEvent;
      startNewStroke(locationX, locationY);
      if (Platform.OS === 'web' && paperRef.current && paperReady) {
        const p = paperRef.current;
        const path = new p.Path();
        const gradColor = getPaperStrokeColor();
        path.strokeColor = gradColor || new p.Color(1, 1, 1, 1);
        path.strokeWidth = brushSize;
        path.strokeCap = 'round';
        path.strokeJoin = 'round';
        // Eraser uses destination-out compositing to punch holes in the overlay
        if (isErasing) {
          path.blendMode = 'destination-out';
        }
        path.add(new p.Point(locationX, locationY));
        paperPathsRef.current.push(path);
      }
    },
    onPanResponderMove: (evt) => {
      const { locationX, locationY } = evt.nativeEvent;
      addPointToCurrentStroke(locationX, locationY);
      if (Platform.OS === 'web' && paperRef.current && paperReady && paperPathsRef.current.length > 0) {
        const p = paperRef.current;
        const path = paperPathsRef.current[paperPathsRef.current.length - 1];
        path.add(new p.Point(locationX, locationY));
        p.view.update();
      }
    },
    onPanResponderRelease: () => {},
    onPanResponderTerminate: () => {},
  }), [isInpainting, startNewStroke, addPointToCurrentStroke, brushSize, displayedRect.left, displayedRect.top, paperReady]);

  const handleUndo = useCallback(() => {
    setStrokes(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const removed = updated.pop();
      setRedoStack(r => [removed, ...r]);
      rebuildPaperFrom(updated);
      return updated;
    });
  }, [rebuildPaperFrom]);

  const handleRedo = useCallback(() => {
    setRedoStack(prev => {
      if (prev.length === 0) return prev;
      const updatedRedo = [...prev];
      const next = updatedRedo.shift();
      if (next) {
        setStrokes(s => {
          const combined = [...s, next];
          rebuildPaperFrom(combined);
          return combined;
        });
      }
      return updatedRedo;
    });
  }, [rebuildPaperFrom]);

  // replaced by function resetInpainting()

  const pointsToPath = (points) => {
    if (!points || points.length === 0) return '';
    const [{ x: startX, y: startY }, ...rest] = points;
    const move = `M ${startX} ${startY}`;
    const lines = rest.map(p => `L ${p.x} ${p.y}`).join(' ');
    return `${move} ${lines}`;
  };

  const handleCancel = useCallback(() => {
    resetInpainting();
    setIsErasing(false);
    setIsInpainting(false);
  }, []);

  const handleDone = useCallback(async () => {
    if (!maskRef.current || imageNaturalSize.width === 0 || imageNaturalSize.height === 0) return;
    try {
      const base64 = await captureRef(maskRef.current, { result: 'base64', format: 'png', quality: 1, width: Math.round(imageNaturalSize.width), height: Math.round(imageNaturalSize.height) });
      if (Platform.OS === 'web') {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${base64}`;
        link.download = `mask-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        addToast?.(t('lightbox.maskDownloaded'), 'success');
      } else {
        await Clipboard.setImageAsync(base64);
        addToast?.(t('lightbox.maskCopied'), 'success');
      }
    } catch (e) {
      console.error('Error creating mask', e);
      addToast?.(t('lightbox.maskFailed'), 'error');
    } finally {
      setIsErasing(false);
      setIsInpainting(false);
    }
  }, [imageNaturalSize.width, imageNaturalSize.height, addToast]);

  // Handle sending message from lightbox chat input
  const handleSend = useCallback(async (formattedMessage) => {
    if (!socketManager.isSocketConnected()) {
      addToast(t('lightbox.connectionError'), 'error', [], { category: 'connection' });
      return false;
    }

    if (!conversationId) {
      addToast(t('lightbox.noConversation'), 'error');
      return false;
    }

    // Build the final message
    let finalMessage = { ...formattedMessage };

    // Ensure editFileIds is set if we have a current file
    if (currentFile?.id) {
      const existingEditFileIds = finalMessage.editFileIds || [];
      if (!existingEditFileIds.includes(currentFile.id)) {
        finalMessage.editFileIds = [currentFile.id, ...existingEditFileIds];
      }
    }

    // If there are inpainting strokes, capture the mask and add to parameters
    if (strokes.length > 0 && maskRef.current && imageNaturalSize.width > 0 && imageNaturalSize.height > 0) {
      try {
        const maskBase64 = await captureRef(maskRef.current, {
          result: 'base64',
          format: 'png',
          quality: 1,
          width: Math.round(imageNaturalSize.width),
          height: Math.round(imageNaturalSize.height),
        });
        finalMessage = {
          ...finalMessage,
          parameters: {
            ...finalMessage.parameters,
            imageMask: `data:image/png;base64,${maskBase64}`,
          },
        };
      } catch (e) {
        console.error('Error capturing mask:', e);
        addToast(t('lightbox.captureMaskFailed'), 'error');
        return false;
      }
    }

    socketManager.emitSendUserMessage({
      userId,
      formattedMessage: finalMessage,
      conversationId,
    });

    // Reset inpainting state and close lightbox after send
    resetInpainting();
    setIsErasing(false);
    setIsInpainting(false);
    onClose();
    return true;
  }, [conversationId, userId, addToast, strokes.length, imageNaturalSize.width, imageNaturalSize.height, onClose, currentFile?.id]);

  // Initialize Paper.js when entering inpainting on web (dynamic import of bundled browser build)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!(Platform.OS === 'web' && isInpainting && canvasRef.current && displayedRect.width > 0 && displayedRect.height > 0)) {
        if (!cancelled) setPaperReady(false);
        return;
      }
      try {
        const mod = await import('@local/paper-full');
        const p = (mod && (mod.default || mod)) || (typeof window !== 'undefined' ? window.paper : null);
        if (cancelled || !p) return;
        paperRef.current = p;
        p.setup(canvasRef.current);
        p.view.viewSize = new p.Size(displayedRect.width, displayedRect.height);
        // Combine overlapping strokes: draw white onto transparent with source-over
        // Paper uses canvas under the hood; ensure compositing is normal
        const ctx = p.view && p.view.context && p.view.context.canvas ? p.view.context : null;
        if (ctx && ctx.ctx) {
          ctx.ctx.globalCompositeOperation = 'source-over';
        }
        if (!cancelled) setPaperReady(true);
      } catch (e) {
        if (!cancelled) setPaperReady(false);
      }
    })();
    return () => {
      cancelled = true;
      try {
        if (paperRef.current) {
          paperRef.current.project.clear();
        }
      } catch (e) {}
      setPaperReady(false);
    };
  }, [isInpainting, displayedRect.width, displayedRect.height]);

  if (!isOpen) return null;
  return (
    <Pressable
      ref={containerRef}
      style={[
        styles.container,
        showSidebar && !isSM && { paddingHorizontal: 96 },
        showSidebar && isSM && { paddingBottom: 96 },
        {
          paddingBottom: withChatInput ? (isSM ? 200 : 120) : 20,
          paddingTop: 20,
        },
      ]}
      onPress={() => {
        if (!dismissOnOutsidePress) return;
        requestClose();
      }}
      {...(Platform.OS === 'macos' ? {
        focusable: true,
        onKeyDown: handleMacOSKeyDown,
      } : {})}
    >
      <View style={styles.topRow}>
        <View style={styles.topRowLeft}>
          <Button
            prependIcon="close"
            variant="transparent"
            size="medium"
            accessibilityLabel={t('lightbox.close')}
            tooltip={t('lightbox.close')}
            tooltipShortcut='Esc'
            hoverBackgroundColor={colors.solid_color_tertiary}
            iconColor={colors.text_color_primary}
            isRound
            onPress={(e) => {
              e.stopPropagation();
              requestClose();
            }}
            style={styles.actionButton}
          />
        </View>
        {/* Center title - only in inpainting mode */}
        {isInpainting && (
          <View style={styles.topRowCenter}>
            <Text size="l" weight="medium" color="primary">
              {t('lightbox.editSelection')}
            </Text>
          </View>
        )}

        <View style={styles.topRowRight}>
          <Button
            prependIcon={isDownloading ? undefined : "arrow-down-to-line"}
            variant="transparent"
            hoverBackgroundColor={colors.solid_color_tertiary}
            size="medium"
            tooltip={Platform.OS === 'web' 
              ? (isCurrent3D ? t('lightbox.downloadModel') : (isCurrentVideo ? t('lightbox.downloadVideo') : (isCurrentAudio ? t('lightbox.downloadAudio') : t('lightbox.downloadImage'))))
              : ((Platform.OS === 'macos' || isCurrent3D || isCurrentAudio) ? t('lightbox.saveToFiles') : t('media.saveToPhotos'))
            }
            iconColor={colors.text_color_primary}
            isRound
            isDisabled={isDownloading}
            onPress={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            style={[styles.actionButton, {
              borderWidth: isXS ? 0 : 1,
              borderColor: colors.solid_color_secondary,
            }]}
          >
            {isDownloading ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <ActivityIndicator size="small" color={colors.text_color_primary} />
                <Text size="s" weight="medium" color="primary">
                  {downloadProgress !== null ? `${Math.round(downloadProgress * 100)}%` : t('media.downloading')}
                </Text>
              </View>
            ) : (
              !isXS ? (
              <Text size="s" weight="medium" color="primary">
                {Platform.OS === 'web' 
                  ? (isCurrent3D ? t('lightbox.downloadModel') : (isCurrentVideo ? t('lightbox.downloadVideo') : (isCurrentAudio ? t('lightbox.downloadAudio') : t('lightbox.downloadImage'))))
                  : ((Platform.OS === 'macos' || isCurrent3D || isCurrentAudio) ? t('lightbox.saveToFiles') : t('media.saveToPhotos'))
                }
              </Text>
              ) : null
            )}
          </Button>
          {canInpaint && !isInpainting && isPlus && !isGhost && (
            <Button
              prependIcon="brush"
              variant="transparent"
              hoverBackgroundColor={colors.solid_color_tertiary}
              size="medium"
              iconColor={colors.text_color_primary}
              tooltip={t('lightbox.inpaintingTool')}
              isRound
              onPress={(e) => {
                e.stopPropagation();
                setIsErasing(false);
                setIsInpainting(true);
              }}
              style={styles.actionButton}
            />
          )}
          {!!conversationId && (
            <Button
              prependIcon="message"
              variant="transparent"
              hoverBackgroundColor={colors.solid_color_tertiary}
              size="medium"
              iconColor={colors.text_color_primary}
              tooltip={t('lightbox.viewInChat')}
              isRound
              onPress={(e) => {
                e.stopPropagation();
                onClose();
                // When the lightbox was opened from this very conversation
                // the route never changes — pushing a duplicate /c/:id entry
                // would make Back revisit the same page before leaving.
                const alreadyInChat = Platform.OS === 'web'
                  && typeof window !== 'undefined'
                  && window.location.pathname === `/c/${conversationId}`;
                if (alreadyInChat) return;
                setTimeout(() => {
                  router.push(`/c/${conversationId}`);
                }, 100);
              }}
              style={styles.actionButton}
            />
          )}
          <Button
            prependIcon="thumbs-down"
            variant="transparent"
            hoverBackgroundColor={colors.solid_color_tertiary}
            size="medium"
            iconColor={colors.text_color_primary}
            tooltip={t('lightbox.feedbackButton')}
            isRound
            onPress={(e) => {
              e.stopPropagation();
              handleFeedback();
            }}
            style={styles.actionButton}
          />
          {Platform.OS !== 'web' && (
            <Button
              prependIcon="share-nodes"
              variant="transparent"
              hoverBackgroundColor={colors.solid_color_tertiary}
              size="medium"
              tooltip={t('media.share')}
              iconColor={colors.text_color_primary}
              isRound
              isDisabled={isDownloading}
              onPress={(e) => {
                e.stopPropagation();
                handleShare();
              }}
              style={styles.actionButton}
            />
          )}
          <Button
            prependIcon="trash"
            variant="transparent"
            hoverBackgroundColor={colors.solid_color_tertiary}
            size="medium"
            tooltip={t('lightbox.delete')}
            iconColor={colors.text_color_primary}
            isRound
            onPress={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            style={styles.actionButton}
          />
          {/* Rename button - only for audio files */}
          {isCurrentAudio && (
            <Button
              prependIcon="pen"
              variant="transparent"
              hoverBackgroundColor={colors.solid_color_tertiary}
              size="medium"
              tooltip="Rename"
              iconColor={colors.text_color_primary}
              isRound
              onPress={(e) => {
                e.stopPropagation();
                handleOpenRename();
              }}
              style={styles.actionButton}
            />
          )}
          {canInpaint && isInpainting && isPlus && !isGhost && (
            <>
              <Button
                prependIcon="rotate-left"
                iconIsSolid={true}
                variant="transparent"
                hoverBackgroundColor={colors.solid_color_tertiary}
                size="medium"
                tooltip={strokes.length === 0 ? undefined : t('lightbox.undo')}
                iconColor={strokes.length === 0 ? colors.text_color_tertiary : colors.text_color_primary}
                isRound
                isDisabled={strokes.length === 0}
                onPress={(e) => {
                  e.stopPropagation();
                  handleUndo();
                }}
                style={styles.actionButton}
              />
              <Button
                prependIcon="rotate-right"
                iconIsSolid={true}
                variant="transparent"
                hoverBackgroundColor={colors.solid_color_tertiary}
                size="medium"
                iconColor={redoStack.length === 0 ? colors.text_color_tertiary : colors.text_color_primary}
                tooltip={redoStack.length === 0 ? undefined : t('lightbox.redo')}
                isRound
                isDisabled={redoStack.length === 0}
                onPress={(e) => {
                  e.stopPropagation();
                  handleRedo();
                }}
                style={styles.actionButton}
              />
              <Button
                prependIcon={isErasing ? "eraser" : "paintbrush"}
                variant="transparent"
                hoverBackgroundColor={colors.solid_color_tertiary}
                size="medium"
                tooltip={isErasing ? t('lightbox.switchToBrush') : t('lightbox.switchToEraser')}
                iconColor={colors.text_color_primary}
                isRound
                onPress={(e) => {
                  e.stopPropagation();
                  setIsErasing(prev => !prev);
                }}
                style={styles.actionButton}
              />
              {/* Brush size slider */}
              <Pressable
                onPress={(e) => e.stopPropagation()}
                style={styles.brushSliderWrapper}
              >
                <View
                  style={[styles.brushTrack, { width: sliderWidth }]}
                  onLayout={(e) => setSliderWidth(Math.max(120, Math.round(e.nativeEvent.layout.width)))}
                  {...sliderResponder.panHandlers}
                >
                  <View style={[styles.brushFill, { width: valueToPos(brushSize) }]} />
                  <View style={[styles.brushThumb, { left: Math.max(0, valueToPos(brushSize) - 10) }]} />
                </View>
              </Pressable>
              <Button
                prependIcon="xmark"
                variant="transparent"
                hoverBackgroundColor={colors.solid_color_tertiary}
                size="medium"
                iconColor={colors.text_color_primary}
                tooltip={t('lightbox.exitInpainting')}
                isRound
                onPress={(e) => {
                  e.stopPropagation();
                  if (strokes.length > 0) {
                    if (Platform.OS === 'web') {
                      const ok = window.confirm(t('lightbox.discardCloseMessage'));
                      if (ok) {
                        try { resetInpainting(); } catch (err) {}
                        setIsErasing(false);
                        setIsInpainting(false);
                      }
                    } else {
                      Alert.alert(
                        t('lightbox.discardTitle'),
                        t('lightbox.discardMessage'),
                        [
                          { text: t('lightbox.keepEditing'), style: 'cancel' },
                          { text: t('lightbox.discardButton'), style: 'destructive', onPress: () => { try { resetInpainting(); } catch (err) {} setIsErasing(false); setIsInpainting(false); } },
                        ]
                      );
                    }
                  } else {
                    handleCancel();
                  }
                }}
                style={styles.actionButton}
              />
              <Button
                prependIcon="check"
                variant="transparent"
                hoverBackgroundColor={colors.solid_color_tertiary}
                size="medium"
                iconColor={colors.text_color_primary}
                tooltip={t('lightbox.saveInpainting')}
                isRound
                onPress={(e) => {
                  e.stopPropagation();
                  handleDone();
                }}
                style={styles.actionButton}
              />
            </>
          )}
        </View>
      </View>

      {isCurrent3D ? (
        <View
          style={styles.imageContainer}
          onLayout={(e) => {
            const { width, height } = e.nativeEvent.layout;
            setContainerSize({ width, height });
          }}
        >
          <Model3DViewer
            modelUrl={currentUrl}
            posterUrl={currentFile?.thumbnailPath || null}
            iosSrc={current3DUsdzSrc}
            alternateFormats={current3DAlternateFormats}
            autoRotate
            style={{ width: '100%', height: '100%' }}
          />
        </View>
      ) : isCurrentVideo ? (
        <View
          style={styles.imageContainer}
          onLayout={(e) => {
            const { width, height } = e.nativeEvent.layout;
            setContainerSize({ width, height });
          }}
        >
          <VideoContent uri={currentUrl} setIsLoading={setIsLoading} />
        </View>
      ) : (
      <Pressable
        style={styles.imageContainer}
        onPress={(e) => e.stopPropagation()}
        onLayout={(e) => {
          const { width, height } = e.nativeEvent.layout;
          setContainerSize({ width, height });
        }}
      >
        {isCurrentAudio ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 20 }}>
            <AudioWaveformPlayer
              isLarge={true}
              sourceUrl={currentUrl}
              size={Math.max(260, Math.round(containerSize.width - 40))}
              file={currentFile}
            />
          </View>
        ) : (
          <>
            {isLoading && (
              <View style={styles.activityIndicator}>
                <ActivityIndicator size="large" color="#ffffff" />
              </View>
            )}
            {Platform.OS === 'macos' ? (
              <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                <RNImage
                  source={{ uri: currentUrl || undefined }}
                  style={{
                    width: displayedRect.width || '100%',
                    height: displayedRect.height || '100%',
                    borderRadius: isSM ? 0 : 18,
                  }}
                  onLoadStart={() => setIsLoading(true)}
                  onLoadEnd={() => setIsLoading(false)}
                />
              </View>
            ) : (
              <MediaImagePreview
                uri={currentUrl || undefined}
                file={currentFile}
                style={styles.image}
                contentFit="contain"
                placeholder={currentFile?.blurhash ? { blurhash: currentFile?.blurhash } : undefined}
                transition={200}
                lazy={false}
                onLoadStart={() => setIsLoading(true)}
                onLoadEnd={() => setIsLoading(false)}
              />
            )}
          </>
        )}

        {/* Drawing overlay (preview) */}
        {canInpaint && isInpainting && displayedRect.width > 0 && (
          <View
            style={[styles.drawArea, { left: displayedRect.left, top: displayedRect.top, width: displayedRect.width, height: displayedRect.height }]}
            {...panResponder.panHandlers}
            onMouseEnter={() => { if (Platform.OS === 'web') setIsHovering(true); }}
            onMouseLeave={() => { if (Platform.OS === 'web') { setIsHovering(false); setHoverPos({ x: -1000, y: -1000 }); } }}
            onMouseMove={(e) => {
              if (Platform.OS === 'web') {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                setHoverPos({ x, y });
              }
            }}
          >
            {/* Web: Paper.js canvas overlay */}
            {Platform.OS === 'web' && (
              <canvas ref={canvasRef} width={displayedRect.width} height={displayedRect.height} style={styles.paperCanvas} />
            )}
            {/* Native / fallback: SVG overlay (opaque strokes + layer opacity). For eraser, we simulate erase by not rendering erase-mode strokes here (preview remains additive). */}
            {Platform.OS !== 'web' && (
              <View style={{ opacity: 0.9 }}>
                <Svg width={displayedRect.width} height={displayedRect.height}>
                  {strokes.map((stroke, idx) => (
                    stroke.mode === 'erase' ? null : (
                      <Path
                        key={`s-${idx}`}
                        d={pointsToPath(stroke.points)}
                        stroke="#ffffff"
                        strokeWidth={stroke.width || brushSize}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    )
                  ))}
                </Svg>
              </View>
            )}

            {Platform.OS === 'web' && isInpainting && isHovering && (
              <View
                style={[
                  styles.brushPreview,
                  {
                    width: brushSize,
                    height: brushSize,
                    borderRadius: brushSize / 2,
                    left: hoverPos.x - brushSize / 2,
                    top: hoverPos.y - brushSize / 2,
                  },
                ]}
              />
            )}
          </View>
        )}

        {/* Hidden mask renderer for export */}
        <View
          ref={maskRef}
          style={[styles.hiddenMaskShot, { width: Math.max(1, Math.round(imageNaturalSize.width)), height: Math.max(1, Math.round(imageNaturalSize.height)) }]}
        >
          <View style={{ width: '100%', height: '100%', backgroundColor: '#000' }}>
            <Svg width={Math.max(1, Math.round(imageNaturalSize.width))} height={Math.max(1, Math.round(imageNaturalSize.height))}>
              {strokes.map((stroke, idx) => (
                <Path
                  key={`m-${idx}`}
                  d={toScaledPath(stroke.points)}
                  stroke="#fff"
                  strokeWidth={(stroke.width || brushSize) * scaleFactors.sx}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              ))}
            </Svg>
          </View>
        </View>
      </Pressable>
      )}
      {/* Right sidebar with assistant images */}
      {!!asstMediaInConversation?.length && (
        <View style={[isSM ? styles.sidebarBottom : styles.sidebar, isSM && { height: 66 + insets.bottom, paddingBottom: 10 + insets.bottom }]} pointerEvents={'auto'}>
          <ScrollView
            contentContainerStyle={styles.sidebarScroll}
            showsVerticalScrollIndicator={!isSM}
            showsHorizontalScrollIndicator={isSM}
            horizontal={isSM}
          >
            {asstMediaInConversation.map((f, idx) => {
              const isVideo = f && (f.fileType === 'video' || f.type === 'video' || f.type?.startsWith?.('video/'));
              const isImage = f && (f.fileType === 'image' || f.type === 'image' || f.type?.startsWith?.('image/'));
              const isAudio = f && (f.fileType === 'audio' || f.type === 'audio' || f.type?.startsWith?.('audio/'));
              const is3D = f && (f.fileType === '3d' || f.type === '3d' || MODEL_3D_EXT_REGEX.test(f.cdnPath || f.url || ''));
              const mediaUri = f && (f.cdnPath || f.url);
              const thumbUri = isVideo
                ? (f?.thumbnailPath || null)
                : is3D
                  ? (f?.thumbnailPath || null)
                  : (isImage ? mediaUri : null);
              if (!thumbUri && !mediaUri) return null;
              const isSelected = currentIndex >= 0 ? (idx === currentIndex) : (mediaUri === currentUrl);
              const size = isSelected ? (isSM ? 46 : 54) : (isSM ? 36 : 42);
              const onThumbPress = (e) => {
                e.stopPropagation();
                if (isInpainting && strokes.length > 0) {
                  if (Platform.OS === 'web') {
                    const ok = window.confirm(t('lightbox.discardSwitchMessage'));
                    if (!ok) return;
                  } else {
                    Alert.alert(
                      t('lightbox.discardTitle'),
                      t('lightbox.discardSwitchMessage'),
                      [
                        { text: t('lightbox.cancel'), style: 'cancel' },
                        { text: t('lightbox.discardButton'), style: 'destructive', onPress: () => { resetInpainting(); setIsInpainting(false); setCurrentIndex(idx); setCurrentUrl(mediaUri || null); } },
                      ]
                    );
                    return;
                  }
                  resetInpainting();
                  setIsInpainting(false);
                }
                setCurrentIndex(idx);
                setCurrentUrl(mediaUri || null);
              };
              return (
                <Pressable
                  key={`asst-img-${idx}`}
                  style={[styles.thumbItem, { width: size, height: size, borderColor: isSelected ? colors.border_color_secondary : getShadowThemeStyles(theme).primaryOpaque(0.2), marginRight: isSM ? 8 : 0 }]}
                  onPress={onThumbPress}
                >
                  {thumbUri ? (
                    <Image
                      source={{ uri: thumbUri }}
                      style={{ width: size, height: size, opacity: isSelected ? 1 : 0.5 }}
                      contentFit="cover"
                      placeholder={f?.blurhash ? { blurhash: f?.blurhash } : undefined}
                      transition={150}
                    />
                  ) : isVideo && mediaUri ? (
                    <VideoThumb uri={mediaUri} size={size} isSelected={isSelected} />
                  ) : (
                    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center', backgroundColor: getShadowThemeStyles(theme).primaryOpaque(0.25) }}>
                      <Icon name={is3D ? 'cube' : (isVideo ? 'play-circle' : (isAudio ? 'music' : 'image'))} size={Math.max(16, Math.round(size * 0.5))} color={colors.text_color_secondary} />
                    </View>
                  )}
                </Pressable>
              );
            })}
          </ScrollView>
          {!isSM && (
            <>
              <LinearGradient
                colors={[getShadowThemeStyles(theme).primaryOpaque(0.7), getShadowThemeStyles(theme).primaryOpaque(0)]}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={styles.sidebarGradientTop}
              />
              <LinearGradient
                colors={[getShadowThemeStyles(theme).primaryOpaque(0), getShadowThemeStyles(theme).primaryOpaque(0.7)]}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={styles.sidebarGradientBottom}
              />
            </>
          )}
        </View>
      )}

      {/* Bottom ChatInput overlay */}
      {withChatInput && canInpaint && isPlus && !isGhost && (
      <View
        style={styles.chatInputContainer}
        pointerEvents={'auto'}
        onStartShouldSetResponderCapture={() => true}
      >
        <Pressable
          style={styles.chatInputInner}
          onPress={(e) => e.stopPropagation()}
          onPressIn={(e) => e.stopPropagation()}
        >
          <ChatInput
            key={'lightbox-input'}
            forceInputStyle='floating'
            isLoading={false}
            forcePlaceholder={true}
            placeholder={isSM ? t('lightbox.describeEdits') : t('lightbox.describeChanges')}
            isInferencing={false}
            withModels={true}
            modelInputs={['image']}
            modelOutputs={['image']}
            hideParameterIds={['files', 'tools']}
            editFileIds={currentFile?.id ? [currentFile.id] : []}
            conversationId={conversationId}
            {...chatInputProps}
            onSend={handleSend}
          />
        </Pressable>
      </View>
      )}

      {/* Rename Modal for Audio Files */}
      {isRenameModalOpen && (
        <View 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001,
          }}
          onStartShouldSetResponder={() => true}
          onResponderRelease={handleCloseRename}
        >
          <Pressable
            style={{
              width: '90%',
              maxWidth: 400,
              backgroundColor: colors.solid_color_primary,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: colors.border_color_secondary,
              overflow: 'hidden',
            }}
            onStartShouldSetResponder={() => true}
            onResponderRelease={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 16,
              borderBottomWidth: 1,
              borderBottomColor: colors.border_color_secondary,
            }}>
              <Text size="m" weight="bold">{t('lightbox.renameTrack')}</Text>
              <Pressable onPress={handleCloseRename}>
                <Icon name="xmark" size={18} color={colors.text_color_secondary} />
              </Pressable>
            </View>
            
            {/* Content */}
            <View style={{ padding: 16 }}>
              <TextInput
                style={{
                  backgroundColor: colors.solid_color_secondary,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.border_color_secondary,
                  padding: 12,
                  fontSize: 14,
                  color: colors.text_color_primary,
                }}
                value={renameValue}
                onChangeText={setRenameValue}
                placeholder={t('lightbox.trackName')}
                placeholderTextColor={colors.text_color_tertiary}
                autoFocus
                selectTextOnFocus
                onSubmitEditing={handleSaveRename}
              />
            </View>
            
            {/* Footer */}
            <View style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 8,
              padding: 16,
              borderTopWidth: 1,
              borderTopColor: colors.border_color_secondary,
            }}>
              <Button
                text={t('lightbox.cancel')}
                variant="secondary"
                size="medium"
                onPress={handleCloseRename}
              />
              <Button
                text={t('lightbox.save')}
                variant="primary"
                size="medium"
                onPress={handleSaveRename}
                isDisabled={!renameValue.trim()}
              />
            </View>
          </Pressable>
        </View>
      )}
    </Pressable>
  );
}

export default Lightbox;

// Inline subcomponent for rendering video using expo-video
function VideoContent({ uri, setIsLoading }) {
  const player = useVideoPlayer(uri ?? null, (p) => {
    p.loop = false;
    p.muted = false;
  });
  const { status } = useEvent(player, 'statusChange', { status: player.status });
  const { isPlaying } = useEvent(player, 'playingChange', { isPlaying: player.playing });
  const [showControls, setShowControls] = React.useState(true);
  const hideTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (!status || status === 'loading') {
      setIsLoading?.(true);
    } else {
      setIsLoading?.(false);
    }
  }, [status, setIsLoading]);

  React.useEffect(() => {
    if (isPlaying) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 2500);
    } else {
      setShowControls(true);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [isPlaying]);

  React.useEffect(() => {
    return () => {
      try { player.pause(); } catch (e) {}
    };
  }, [player]);

  const handlePlayPause = React.useCallback(() => {
    try {
      if (player.playing) {
        player.pause();
      } else {
        player.play();
      }
    } catch (e) {}
  }, [player]);

  const handleTapVideo = React.useCallback(() => {
    if (isPlaying) {
      setShowControls(prev => !prev);
      if (!showControls) {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setShowControls(false), 2500);
      }
    } else {
      handlePlayPause();
    }
  }, [isPlaying, showControls, handlePlayPause]);

  return (
    <View style={{ flex: 1, width: '100%', height: '100%' }}>
      <VideoView
        player={player}
        style={{ width: '100%', height: '100%', borderRadius: 18 }}
        nativeControls={Platform.OS !== 'android'}
        allowsFullscreen={true}
        allowsPictureInPicture={Platform.OS !== 'android'}
        contentFit="contain"
      />
      {(status === 'loading') && (
        <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="large" color="#ffffff" />
        </View>
      )}
      {Platform.OS === 'android' && status !== 'loading' && (
        <Pressable
          onPress={handleTapVideo}
          style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}
        >
          {(showControls || !isPlaying) && (
            <View style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={isPlaying ? 'pause' : 'play'} size={32} color="#ffffff" />
            </View>
          )}
        </Pressable>
      )}
    </View>
  );
}

// Inline subcomponent for rendering a tiny video preview when no thumbnail exists
function VideoThumb({ uri, size, isSelected }) {
  const player = useVideoPlayer(uri ?? null, (p) => {
    p.loop = false;
    p.muted = true;
    p.pause();
  });

  React.useEffect(() => {
    return () => {
      try { player.pause(); } catch (e) {}
    };
  }, [player]);

  return (
    <VideoView
      player={player}
      style={{ width: size, height: size, borderRadius: 6, opacity: isSelected ? 1 : 0.5 }}
      nativeControls={false}
      allowsFullscreen={false}
      allowsPictureInPicture={false}
      contentFit="cover"
    />
  );
}
